import { APP_VERSION } from "../constants/app";

export type AnalyticsParamValue = string | number | boolean | null | undefined;

export type AnalyticsParams = Record<string, AnalyticsParamValue>;

type SanitizedAnalyticsParams = Record<string, string | number>;

const PLATFORM = "institucional";
const ENVIRONMENT = "production";
const PRODUCTION_MEASUREMENT_ID = "G-47LVY79GP4";
const REDACTED_VALUE = "[redacted]";

const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const CPF_PATTERN = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
const PHONE_PATTERN = /(?:\+?55[\s.-]?)?\(?\d{2}\)?[\s.-]?(?:9[\s.-]?)?\d{4}[\s.-]?\d{4}\b/g;

const SENSITIVE_PARAM_NAMES = [
  "cpf",
  "email",
  "e-mail",
  "telefone",
  "phone",
  "celular",
  "matricula",
  "matrícula",
  "user_id",
  "userid",
];

const containsSensitiveParamName = (paramName: string): boolean => {
  const normalizedParamName = paramName.toLowerCase();

  return SENSITIVE_PARAM_NAMES.some((sensitiveName) =>
    normalizedParamName.includes(sensitiveName)
  );
};

const sanitizeString = (value: string): string =>
  value
    .replace(EMAIL_PATTERN, REDACTED_VALUE)
    .replace(CPF_PATTERN, REDACTED_VALUE)
    .replace(PHONE_PATTERN, REDACTED_VALUE);

export const sanitizeParams = (
  params: AnalyticsParams = {}
): SanitizedAnalyticsParams =>
  Object.entries(params).reduce<SanitizedAnalyticsParams>(
    (sanitizedParams, [paramName, value]) => {
      if (
        value === null ||
        value === undefined ||
        containsSensitiveParamName(paramName)
      ) {
        return sanitizedParams;
      }

      if (typeof value === "boolean") {
        sanitizedParams[paramName] = value ? "true" : "false";
        return sanitizedParams;
      }

      if (typeof value === "number") {
        if (!Number.isFinite(value)) return sanitizedParams;

        const stringValue = String(value);
        sanitizedParams[paramName] =
          sanitizeString(stringValue) === stringValue ? value : REDACTED_VALUE;
        return sanitizedParams;
      }

      sanitizedParams[paramName] = sanitizeString(value);
      return sanitizedParams;
    },
    {}
  );

const isLocalEnvironment = (): boolean =>
  process.env.NODE_ENV === "development";

export const isAnalyticsEnabled = (): boolean =>
  process.env.NODE_ENV === "production" &&
  process.env.REACT_APP_GOOGLE_GA_TAG === PRODUCTION_MEASUREMENT_ID;

export const track = (
  eventName: string,
  params: AnalyticsParams = {}
): void => {
  try {
    if (!eventName.trim()) return;

    const eventParams: SanitizedAnalyticsParams = {
      ...sanitizeParams(params),
      plataforma: PLATFORM,
      ambiente: ENVIRONMENT,
      app_version: APP_VERSION,
    };

    if (isLocalEnvironment()) {
      console.log("[analytics]", eventName, eventParams);
      return;
    }

    if (!isAnalyticsEnabled()) return;
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;

    window.gtag("event", eventName, eventParams);
  } catch (error) {
    if (isLocalEnvironment()) {
      console.warn("[analytics] Falha ao processar evento", error);
    }
  }
};
